// Cheap, continuous health pulse — staleness only, no full drift check.
//
// Adapted from mex (MIT, Copyright (c) 2026 Daksh Jaitly). Re-implemented on
// CAI's git-based staleness (daysSinceLastChange) instead of mex's frontmatter
// last_updated + memory layer. Prints HEARTBEAT_OK when clean, else the stale
// scaffold files. Intended for a pre-commit / cron pulse, lighter than `cai check`.

import { relative } from "node:path";
import chalk from "chalk";
import { findScaffoldFiles } from "./drift/index.js";
import { daysSinceLastChange } from "./git.js";
import type { CaiConfig } from "./types.js";

const DEFAULT_STALE_DAYS = 7;

export interface HeartbeatResult {
  ok: boolean;
  staleFiles: Array<{ file: string; days: number }>;
}

/** Run the heartbeat: cheap staleness pulse over scaffold files. */
export async function runHeartbeat(
  config: CaiConfig,
  opts: { json?: boolean } = {}
): Promise<HeartbeatResult> {
  const result = await checkHeartbeat(config);
  if (opts.json) {
    console.log(JSON.stringify(result, null, 2));
  } else {
    printHeartbeat(result, config);
  }
  return result;
}

/** Compute the heartbeat result without printing. */
export async function checkHeartbeat(config: CaiConfig): Promise<HeartbeatResult> {
  const staleDays = config.settings.heartbeat?.staleDays ?? DEFAULT_STALE_DAYS;
  const files = findScaffoldFiles(config.projectRoot, config.scaffoldRoot);

  const staleFiles: Array<{ file: string; days: number }> = [];
  for (const filePath of files) {
    const source = relative(config.projectRoot, filePath);
    const days = await daysSinceLastChange(source, config.projectRoot);
    if (days !== null && days > staleDays) {
      staleFiles.push({ file: source, days });
    }
  }

  return { ok: staleFiles.length === 0, staleFiles };
}

function printHeartbeat(result: HeartbeatResult, config: CaiConfig): void {
  if (result.ok) {
    console.log("HEARTBEAT_OK");
    return;
  }

  console.log(chalk.bold("Heartbeat needs attention"));
  console.log();
  console.log(chalk.yellow("Stale scaffold files:"));
  for (const f of result.staleFiles) {
    console.log(`  ${f.file} — last changed ${f.days} days ago`);
  }
  console.log(
    chalk.dim("  Review these files and run `cai sync` if they no longer match reality.")
  );
  console.log();
  console.log(chalk.dim(`Scaffold: ${config.scaffoldRoot}`));
}
